import React, { useEffect, useState } from 'react';
import { LIMITES, avisaDelTope } from '../limites';
import { PROFESIONES, PROFESIONES_A_LA_VISTA } from '../profesiones';
import './OnboardingPage.css';
import { DESTINO_DEL_ALUMNO } from '../sesion.js';

// ── LA PREGUNTA DE LA PROFESIÓN, EN UN SOLO SITIO ───────────────────────────
//  Se hace en dos pantallas —el cuestionario largo y el corto de quien ya
//  contestó en la puerta— y las dos tienen que preguntar IGUAL. Escrita dos
//  veces, el día que se toque una se queda la otra como estaba (Rob ya nos
//  encontró ese fallo con los topes de texto).
//
//  Rob, 23-09-2026: «estamos limitando a esas opciones, deberíamos tener un
//  input que diga escribe a qué te dedicas, obviamente más opciones, y el input
//  si no está en sus opciones».
//
//  Así que aquí manda la CAJA DE TEXTO: lleva su etiqueta escrita, su contador
//  y su ejemplo. Las sugerencias son atajos que la rellenan, se ve cuál está
//  elegida, y hay muchas más que antes.
const PreguntaProfesion = ({ valor, onCambio }) => {
  const [verTodas, setVerTodas] = useState(false);
  const tope = LIMITES.profesion;
  const lista = verTodas ? PROFESIONES : PROFESIONES.slice(0, PROFESIONES_A_LA_VISTA);
  const elegida = valor.trim().toLowerCase();

  return (
    <div className="campo-profesion">
      <div className="campo-cabecera">
        <label className="campo-etiqueta" htmlFor="profesion">
          Escribe a qué te dedicas
        </label>
        {/* El contador se enseña SIEMPRE: con la caja vacía es la única forma
            de saber cuánto cabe antes de chocarse con el tope. */}
        <span className={`campo-contador ${avisaDelTope(valor.length, tope) ? 'cerca' : ''}`}>
          {valor.length}/{tope}
        </span>
      </div>
      <input
        id="profesion"
        name="profession"
        type="text"
        className="neuro-input large"
        placeholder="enfermera, mesero, estudiante…"
        maxLength={tope}
        value={valor}
        /* El recorte va aquí, en el único punto por el que pasa todo lo que
           entra en el campo. `maxLength` solo frena al que teclea. */
        onChange={(e) => onCambio(e.target.value.slice(0, tope))}
        data-prueba="profesion"
      />
      <p className="campo-ayuda">
        Si no está en la lista, escríbela. Las de abajo son atajos.
      </p>
      <div className="oficios">
        {lista.map((prof) => (
          <button
            type="button"
            key={prof}
            data-prueba="sugerencia"
            className={`oficio ${elegida === prof.toLowerCase() ? 'elegido' : ''}`}
            onClick={() => onCambio(prof)}
          >
            {prof}
          </button>
        ))}
        {!verTodas && (
          <button
            type="button"
            className="oficio oficio-mas"
            data-prueba="ver-mas"
            onClick={() => setVerTodas(true)}
          >
            + Ver más oficios
          </button>
        )}
      </div>
    </div>
  );
};

// ── CUANDO EL ALUMNO YA CONTESTÓ EN LA PUERTA ───────────────────────────────
//  El diagnóstico de `/` pregunta su objetivo, su contexto y cómo se ve de
//  nivel, y al registrarse el servidor le crea el perfil con eso. Entonces de
//  estas seis pantallas solo falta UNA: la profesión, que el diagnóstico no
//  pregunta. Pasarle otra vez el cuestionario entero era repetirle, cinco
//  minutos después, lo que acababa de decir (Rob, 22-09-2026).
const OnboardingPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // `null` mientras se le pregunta al servidor: sin esto se ve un parpadeo del
  // cuestionario largo antes de saber que no hacía falta.
  const [soloProfesion, setSoloProfesion] = useState(null);
  const [error, setError] = useState('');
  const totalSlides = 7;

  const [formData, setFormData] = useState({
    profession: '',
    learning_focus: '',
    speaking_confidence: '',
    purposes: [],
    other_purpose: '',
    example_context: '',
  });

  // ── EL TEMA QUE ELIGIÓ ────────────────────────────────────────────────────
  //  Quien aplica `data-theme` en NSL es la CABECERA, y esta pantalla no tiene
  //  ninguna. Resultado medido el 26-09-2026: con el tema oscuro guardado, esta
  //  pantalla salía clara igualmente — un fogonazo blanco en mitad del alta,
  //  justo entre dos pantallas que sí respetan su elección.
  useEffect(() => {
    const tema = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', tema);
    document.body.setAttribute('data-theme', tema);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setSoloProfesion(false); return; }
    fetch('/api/profile/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const p = d && d.learning_profile;
        if (p && !p.profession) {
          // Ya sabemos lo demás: se rellena para no perderlo al guardar.
          setFormData(f => ({
            ...f,
            learning_focus:      p.learning_focus || '',
            speaking_confidence: p.speaking_confidence || '',
            purposes:            p.purposes || [],
            other_purpose:       p.other_purpose || '',
            example_context:     p.example_context || '',
          }));
          setSoloProfesion(true);
        } else {
          setSoloProfesion(false);
        }
      })
      .catch(() => setSoloProfesion(false));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const ponProfesion = (texto) => {
    setFormData(prevData => ({ ...prevData, profession: texto }));
  };

  const handleOptionSelect = (field, value) => {
    setFormData(prevData => ({ ...prevData, [field]: value }));
    setTimeout(nextSlide, 200);
  };

  const handleMultiSelect = (value) => {
    setFormData(prevData => {
      const newPurposes = prevData.purposes.includes(value)
        ? prevData.purposes.filter(p => p !== value)
        : [...prevData.purposes, value];
      return { ...prevData, purposes: newPurposes };
    });
  };

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  // ── VOLVER ────────────────────────────────────────────────────────────────
  //  Rob, 23-09-2026: «no tenemos para ir atrás por si se equivocó, solo
  //  tenemos continuar». Tres de estas pantallas avanzan solas al tocar una
  //  opción, así que un dedo mal puesto dejaba una respuesta equivocada metida
  //  en el perfil para siempre: no había forma de corregirla desde aquí.
  const prevSlide = () => {
    setError('');
    setCurrentSlide(prev => (prev > 0 ? prev - 1 : 0));
  };

  const handleSubmit = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Error: No se encontró tu sesión. Por favor, inicia sesión de nuevo.');
      setIsLoading(false);
      return;
    }

    try {
      // Si el perfil ya existe —porque vino del diagnóstico— se ACTUALIZA; si
      // no, se crea. Llamar a «completar» sobre un perfil que ya está devuelve
      // 400, y el alumno se quedaría encallado en esta pantalla.
      const response = await fetch(
        soloProfesion ? '/api/profile/me/learning-profile' : '/api/profile/onboarding/complete', {
        method: soloProfesion ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // ── AQUÍ ES DONDE MÁS SE NOTA ────────────────────────────────────
        //  Esta pantalla es lo último del alta: quien la termina acaba de
        //  crear su cuenta. Mandarle al catálogo del curso era dejarle
        //  delante de una estantería con la que no sabe qué hacer; ahora le
        //  recibe Spacy, que le saluda por su nombre y le enseña la casa
        //  entera antes de que tenga que decidir nada (Rob, 27-09-2026).
        //
        //  `window.location.href` y no `navigate`: al terminar el alta
        //  interesa recargar la app para que el perfil recién creado lo lean
        //  todos los guardias desde cero.
        window.location.href = DESTINO_DEL_ALUMNO;
      } else {
        // Un `alert` bloquea la pantalla y no dice nada útil; el error se
        // enseña donde está mirando, y con un texto que se entiende.
        const errorData = await response.json().catch(() => ({}));
        const d = errorData.detail;
        setError(typeof d === 'string' ? d
          : 'No pudimos guardar tu perfil. Inténtalo de nuevo en un momento.');
      }
    } catch {
      setError('No hay conexión. Revisa tu internet e inténtalo otra vez.');
    } finally {
      setIsLoading(false);
    }
  };

  const sinProfesion = !formData.profession.trim();

  const slides = [
    // DIAPOSITIVA 0: BIENVENIDA
    <div key={0} className="slide-content">
      <h1 className="slide-title">¡Bienvenido a tu viaje de aprendizaje! 🚀</h1>
      <p className="slide-subtitle">Vamos a personalizar tu experiencia en solo unos pasos</p>
      <button className="btn-primary" onClick={nextSlide}>Comenzar</button>
    </div>,

    // DIAPOSITIVA 1: PROFESIÓN
    <div key={1} className="slide-content">
      <h2 className="slide-title">¿A qué te dedicas?</h2>
      <p className="slide-subtitle">
        Con esto Spacy te pone ejemplos de tu mundo, no del de otro.
      </p>
      <PreguntaProfesion valor={formData.profession} onCambio={ponProfesion} />
      {/* Sin profesión el perfil se guarda incompleto y la app vuelve a mandarlo
          aquí al entrar: sería una vuelta infinita. Por eso no se puede pasar
          de largo, y se dice por qué en vez de dejar el botón muerto y mudo. */}
      {sinProfesion && (
        <p className="campo-ayuda centrado">Escribe tu ocupación para continuar.</p>
      )}
      <button
        className="btn-primary"
        style={{ marginTop: '20px' }}
        onClick={nextSlide}
        disabled={sinProfesion}
        data-prueba="continuar"
      >
        Continuar
      </button>
    </div>,

    // DIAPOSITIVA 2: ENFOQUE DEL APRENDIZAJE
    <div key={2} className="slide-content">
      <h2 className="slide-title">¿Te gustaría que tu aprendizaje se base en el vocabulario de tu profesión?</h2>
      <p className="slide-subtitle">Elige cómo quieres enfocar tus clases</p>
      <div className="options-grid">
        {/* Se marca la elegida: al volver atrás, el alumno tiene que VER qué
            contestó, o no sabe qué está corrigiendo. */}
        <button className={`option-btn ${formData.learning_focus === 'professional' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('learning_focus', 'professional')}>💼 Sí, enfocado en mi profesión</button>
        <button className={`option-btn ${formData.learning_focus === 'mixed' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('learning_focus', 'mixed')}>⚖️ 50/50 (profesional y general)</button>
        <button className={`option-btn ${formData.learning_focus === 'general' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('learning_focus', 'general')}>🌎 Prefiero vocabulario general</button>
      </div>
    </div>,

    // DIAPOSITIVA 3: NIVEL DE CONFIANZA AL HABLAR
    <div key={3} className="slide-content">
      <h2 className="slide-title">¿Cómo te sientes al hablar inglés?</h2>
      <p className="slide-subtitle">Indica tu nivel de confianza actual al expresarte</p>
      <div className="confidence-options">
        <button className={`confidence-btn ${formData.speaking_confidence === 'low' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('speaking_confidence', 'low')}>
          <span className="emoji">😰</span>
          <span>Me cuesta mucho expresarme</span>
        </button>
        <button className={`confidence-btn ${formData.speaking_confidence === 'medium' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('speaking_confidence', 'medium')}>
          <span className="emoji">🙂</span>
          <span>Me defiendo, pero me falta práctica</span>
        </button>
        <button className={`confidence-btn ${formData.speaking_confidence === 'high' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('speaking_confidence', 'high')}>
          <span className="emoji">🚀</span>
          <span>Me siento bastante seguro</span>
        </button>
      </div>
    </div>,

    // DIAPOSITIVA 4: PROPÓSITOS
    <div key={4} className="slide-content">
      <h2 className="slide-title">¿Cuáles son tus propósitos para aprender inglés?</h2>
      <p className="slide-subtitle">Puedes seleccionar varias opciones</p>
      <div className="interests-grid">
        {['Mejorar mi carrera', 'Viajar cómodamente', 'Hablar con fluidez', 'Entender cultura', 'Escribir mejor', 'Uso académico'].map(purpose => (
          <button
            key={purpose}
            className={`interest-card ${formData.purposes.includes(purpose) ? 'selected' : ''}`}
            onClick={() => handleMultiSelect(purpose)}
          >
            <div className="interest-label">{purpose}</div>
          </button>
        ))}
      </div>
      <div className="campo-cabecera">
        <label className="campo-etiqueta" htmlFor="otro-proposito">¿Otro propósito? Escríbelo</label>
        <span className={`campo-contador ${avisaDelTope(formData.other_purpose.length, LIMITES.contexto) ? 'cerca' : ''}`}>
          {formData.other_purpose.length}/{LIMITES.contexto}
        </span>
      </div>
      <input
        id="otro-proposito"
        maxLength={LIMITES.contexto}
        type="text"
        className="neuro-input"
        placeholder="Ej: entender las canciones que escucho"
        name="other_purpose"
        value={formData.other_purpose}
        onChange={handleInputChange}
      />
      <button className="btn-primary" style={{ marginTop: '20px' }} onClick={nextSlide}>Continuar</button>
    </div>,

    // DIAPOSITIVA 5: CONTEXTO DE EJEMPLOS
    <div key={5} className="slide-content">
      <h2 className="slide-title">¿En qué contexto te gustaría que se basen tus ejemplos y actividades?</h2>
      <p className="slide-subtitle">Esto ayuda a adaptar el tono y tipo de ejemplos en tus lecciones</p>
      <div className="learning-styles">
        <button className={`style-card ${formData.example_context === 'professional' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('example_context', 'professional')}>
          <div className="style-title">🧑‍💼 Profesional</div>
        </button>
        <button className={`style-card ${formData.example_context === 'educational' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('example_context', 'educational')}>
          <div className="style-title">👨‍🏫 Educativo</div>
        </button>
        <button className={`style-card ${formData.example_context === 'everyday' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('example_context', 'everyday')}>
          <div className="style-title">👨‍👩‍👧 Cotidiano</div>
        </button>
        <button className={`style-card ${formData.example_context === 'youthful' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('example_context', 'youthful')}>
            <div className="style-title">👦 Infantil o juvenil</div>
        </button>
        <button className={`style-card ${formData.example_context === 'mixed' ? 'selected' : ''}`}
          onClick={() => handleOptionSelect('example_context', 'mixed')}>
            <div className="style-title">⚙️ Mixto</div>
        </button>
      </div>
    </div>,

    // DIAPOSITIVA 6: FINAL
    <div key={6} className="slide-content">
      <div className="final-message">
        <h2 className="final-title">¡Todo listo! ✨</h2>
        <p className="final-motivation">Enjoy your learning journey and keep growing every day! 🌟</p>
        <p className="final-resumen">
          Te dedicas a <strong>{formData.profession || '—'}</strong>. Si no es eso,
          toca «Atrás» y cámbialo.
        </p>
      </div>
      {error && <p className="campo-error" role="alert">{error}</p>}
      <button className="btn-primary" onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? 'Guardando...' : 'Ir al curso'}
      </button>
    </div>
  ];

  if (soloProfesion === null) {
    return <div className="wizard-container"><div className="slide-content">Un momento…</div></div>;
  }

  // Ya contestó en la puerta: una pregunta y adentro.
  if (soloProfesion) {
    return (
      <div className="wizard-container" data-prueba="onboarding-corto">
        <div className="slides-wrapper">
          <div className="wizard-slide active">
            <div className="slide-content">
              {/* Chapa y bloque, como el resto de NSL desde el 24-09-2026: quien
                  llega aquí viene del embudo del diagnóstico y no debería sentir
                  que ha cambiado de aplicación a mitad del alta. */}
              <p className="onb-chapa">Último paso</p>
              <h2 className="slide-title">
                <span className="onb-titular-linea">Solo nos falta</span>
                <span className="onb-bloque">una cosa</span>
              </h2>
              <p className="slide-subtitle">
                ¿A qué te dedicas? Con eso, Spacy usa ejemplos de tu mundo.
              </p>
              <PreguntaProfesion valor={formData.profession} onCambio={ponProfesion} />
              {error && <p className="campo-error" role="alert">{error}</p>}
              <button className="btn-primary" style={{ marginTop: '20px' }}
                onClick={handleSubmit} disabled={isLoading || sinProfesion}
                data-prueba="entrar">
                {isLoading ? 'Guardando…' : 'Entrar a entrenar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-container">
      <div className="wizard-topbar">
        {currentSlide > 0 ? (
          <button className="btn-atras" onClick={prevSlide} data-prueba="atras">
            ← Atrás
          </button>
        ) : <span className="btn-atras-hueco" />}
        <span className="wizard-paso">Paso {currentSlide + 1} de {totalSlides}</span>
      </div>
      <div className="progress-container">
        {[...Array(totalSlides)].map((_, i) => (
          <div key={i} className={`progress-dot ${i <= currentSlide ? 'active' : ''}`} />
        ))}
      </div>
      <div className="slides-wrapper">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`wizard-slide ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'prev' : ''}`}
          >
            {slide}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnboardingPage;
